import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CAPABILITY_GROUPS, capabilityLabel, type RoleDto } from '@thebb/shared';
import { del, get, patch, post } from '../api';
import { useAuth } from '../auth';
import { ErrorBox, PageTitle, Spinner } from '../components/ui';

/** The QR2 matrix: capabilities as rows, roles as columns, ticks saved with one button. */
export function RolesPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const data = useQuery({ queryKey: ['roles'], queryFn: () => get<{ roles: RoleDto[]; capabilities: string[] }>('/roles') });
  const [draft, setDraft] = useState<Record<string, Set<string>>>({});
  const [error, setError] = useState<unknown>(null);
  const [newLabel, setNewLabel] = useState('');

  useEffect(() => {
    if (data.data) setDraft(Object.fromEntries(data.data.roles.map((r) => [r.id, new Set(r.caps)])));
  }, [data.data]);

  const save = useMutation({
    mutationFn: async () => {
      for (const r of data.data!.roles) {
        if (r.key === 'admin') continue;
        const caps = [...(draft[r.id] ?? new Set())];
        const same = caps.length === r.caps.length && caps.every((c) => r.caps.includes(c));
        if (!same) await patch(`/roles/${r.id}`, { caps });
      }
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['roles'] }),
    onError: setError,
  });
  const create = useMutation({
    mutationFn: () => post('/roles', { label: newLabel, caps: [] }),
    onSuccess: () => {
      setNewLabel('');
      void qc.invalidateQueries({ queryKey: ['roles'] });
    },
    onError: setError,
  });
  const remove = useMutation({
    mutationFn: (id: string) => del(`/roles/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['roles'] }),
    onError: setError,
  });

  if (data.isLoading || !data.data) return <Spinner />;
  const roles = data.data.roles;
  const editable = can('roles.manage');

  const toggle = (roleId: string, cap: string) => {
    setDraft((d) => {
      const next = new Set(d[roleId] ?? []);
      if (next.has(cap)) next.delete(cap);
      else next.add(cap);
      return { ...d, [roleId]: next };
    });
  };

  return (
    <div>
      <PageTitle
        title="Roller og rettigheder"
        actions={
          editable && (
            <>
              <input className="input w-48" placeholder="Ny rolle" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} />
              <button className="btn" disabled={!newLabel.trim() || create.isPending} onClick={() => create.mutate()}>
                Tilføj rolle
              </button>
              <button className="btn btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>
                Gem ændringer
              </button>
            </>
          )
        }
      />
      <ErrorBox error={error} />
      <div className="card overflow-x-auto p-0">
        <table className="table">
          <thead>
            <tr>
              <th>Rettighed</th>
              {roles.map((r) => (
                <th key={r.id} className="text-center">
                  <div>{r.label}</div>
                  <div className="text-[10px] font-normal normal-case text-zinc-400">
                    {r.userCount} bruger{r.userCount === 1 ? '' : 'e'}
                    {editable && !r.system && r.userCount === 0 && (
                      <button className="ml-1 text-red-600 hover:underline" onClick={() => remove.mutate(r.id)}>
                        slet
                      </button>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(CAPABILITY_GROUPS).map(([group, caps]) => (
              <GroupRows key={group} group={group} caps={Object.keys(caps)} roles={roles} draft={draft} editable={editable} toggle={toggle} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GroupRows({ group, caps, roles, draft, editable, toggle }: { group: string; caps: string[]; roles: RoleDto[]; draft: Record<string, Set<string>>; editable: boolean; toggle: (roleId: string, cap: string) => void }) {
  return (
    <>
      <tr className="bg-zinc-50">
        <td colSpan={roles.length + 1} className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
          {group}
        </td>
      </tr>
      {caps.map((cap) => (
        <tr key={cap}>
          <td>
            <div className="font-mono text-xs">{cap}</div>
            <div className="text-xs text-zinc-500">{capabilityLabel(cap as never)}</div>
          </td>
          {roles.map((r) => {
            const locked = r.key === 'admin' || !editable;
            const checked = r.key === 'admin' ? true : (draft[r.id]?.has(cap) ?? false);
            return (
              <td key={r.id} className="text-center">
                <input type="checkbox" checked={checked} disabled={locked} onChange={() => toggle(r.id, cap)} />
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}
